import { doc, getDoc, collection, getDocs, query, orderBy, limit as limitFn, where, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./config";
import { storage } from './config';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

// Default user photo path
const DEFAULT_USER_PHOTO = '/images/default-user.png';

// جلب مقال بواسطة المعرف للعرض
export const getArticleById = async (id) => {
  try {
    const articleRef = doc(db, 'articles', id);
    const articleSnap = await getDoc(articleRef);
    
    if (!articleSnap.exists()) {
      throw new Error('Article not found');
    }
    
    const articleData = articleSnap.data();
    
    // معالجة حقل التاريخ لتحويله من Timestamp إلى Date
    const createdAt = articleData.createdAt?.toDate ? 
      articleData.createdAt.toDate() : 
      new Date(articleData.createdAt || Date.now());
    
    // الحصول على المحتوى الكامل من رابط التخزين إذا كان متوفرًا
    let fullContent = articleData.content;
    if (articleData.contentUrl) {
      try {
        const contentRef = ref(storage, articleData.contentUrl);
        const contentUrl = await getDownloadURL(contentRef);
        const response = await fetch(contentUrl);
        fullContent = await response.text();
      } catch (error) {
        console.error('Error fetching full content:', error);
      }
    }
    
    // استخراج الصورة من المحتوى إذا لم تكن موجودة
    let articleImage = articleData.image;
    if (!articleImage && fullContent) {
      const imgMatch = fullContent.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch && imgMatch[1]) {
        articleImage = imgMatch[1];
      }
    }
    
    // الحصول على بيانات الكاتب المحدثة
    let authorData = {
      displayName: articleData.author || 'Anonymous',
      photoURL: articleData.authorPhoto || DEFAULT_USER_PHOTO,
      columnTitle: articleData.columnTitle || null
    };
    
    if (articleData.authorId) {
      try {
        const userRef = doc(db, 'users', articleData.authorId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          authorData = {
            displayName: userData.displayName || articleData.author || 'Anonymous',
            photoURL: userData.photoURL || articleData.authorPhoto || DEFAULT_USER_PHOTO,
            columnTitle: userData.columnTitle || articleData.columnTitle || null,
            bio: userData.bio || null
          };
        }
      } catch (error) {
        console.error('Error fetching author data:', error);
      }
    }
    
    // تحديث محتوى المقالة وإعادة القيم المحدثة
    return {
      id: articleSnap.id,
      ...articleData,
      author: authorData.displayName,
      authorPhoto: authorData.photoURL,
      authorImage: authorData.photoURL,
      columnTitle: authorData.columnTitle,
      authorBio: authorData.bio,
      content: fullContent,
      createdAt,
      image: articleImage
    };
  } catch (error) {
    console.error('Error getting article by ID:', error);
    throw error;
  }
};

// جلب قائمة مقالات للصفحة الرئيسية
export const getArticles = async (limit = 10, offset = 0) => {
  try {
    const articlesRef = collection(db, "articles");
    
    // إنشاء استعلام مع ترتيب وتحديد عدد النتائج
    const q = query(
      articlesRef,
      orderBy("createdAt", "desc"),
      limitFn(limit)
    );
    
    const querySnapshot = await getDocs(q);
    const articles = [];
    
    // تخزين المستخدمين المجلوبين لتجنب التكرار
    const fetchedUserData = {};
    
    // جلب المقالات وصورها
    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data();
      let createdAtDate;

      if (data.createdAt) {
        if (data.createdAt.toDate) {
          createdAtDate = data.createdAt.toDate();
        } else if (data.createdAt instanceof Date) {
          createdAtDate = data.createdAt;
        } else if (typeof data.createdAt === 'string') {
          createdAtDate = new Date(data.createdAt);
        } else {
          createdAtDate = new Date(data.createdAt);
        }
      } else {
        createdAtDate = new Date();
      }

      // إذا كان المحتوى في Storage، نقوم بجلبه
      let fullContent = data.content;
      if (data.contentUrl) {
        try {
          const response = await fetch(data.contentUrl);
          fullContent = await response.text();
        } catch (error) {
          console.error("Error fetching full content:", error);
          // في حالة فشل جلب المحتوى الكامل، نستخدم المعاينة المخزنة في Firestore
        }
      }

      // استخراج أول صورة من المحتوى الكامل
      let image = data.image;
      if (!image && fullContent) {
        const imgMatch = fullContent.match(/<img[^>]+src="([^">]+)"/);
        if (imgMatch) {
          image = imgMatch[1];
        }
      }

      // تحديث معلومات الكاتب من وثيقة المستخدم (للتأكد من أحدث البيانات)
      let authorImage = data.authorImage || DEFAULT_USER_PHOTO;
      let authorName = data.author || data.authorName || 'Anonymous';
      let columnTitle = data.columnTitle || 'My Column';
      
      if (data.authorId) {
        try {
          // دائمًا جلب أحدث بيانات المستخدم من Firestore لضمان عدم وجود مشكلات في التخزين المؤقت
          const userDocRef = doc(db, "users", data.authorId);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            // تخزين بيانات المستخدم المحدثة
            fetchedUserData[data.authorId] = userData;
            
            if (userData) {
              // تحديث صورة الكاتب
              if (userData.photoURL) {
                authorImage = userData.photoURL;
              } else {
                authorImage = DEFAULT_USER_PHOTO;
              }
              
              // تحديث اسم العمود
              if (userData.columnTitle) {
                columnTitle = userData.columnTitle;
              }
              
              // تحديث اسم الكاتب
              if (userData.displayName) {
                authorName = userData.displayName;
              }
            }
          }
        } catch (error) {
          console.error("Error fetching author data:", error);
          // استمر باستخدام البيانات المخزنة في المقال نفسه في حالة وجود خطأ
        }
      }

      articles.push({
        id: docSnapshot.id,
        ...data,
        content: fullContent || data.content,
        image: image,
        authorImage: authorImage,
        author: authorName,  // تحديث اسم الكاتب
        authorName: authorName, // للتوافق مع الحقول القديمة والجديدة
        columnTitle: columnTitle, // تحديث اسم العمود
        createdAt: createdAtDate
      });
    }
    
    return articles;
  } catch (error) {
    console.error("Error fetching articles:", error);
    throw error;
  }
};

// وظيفة لتخزين المحتوى في Storage
const storeContent = async (content, articleId) => {
  const contentRef = ref(storage, `articles/${articleId}/content.txt`);
  await uploadString(contentRef, content);
  return await getDownloadURL(contentRef);
};

// تعديل وظيفة إضافة مقال جديد
export const addArticle = async (articleData) => {
  try {
    // نسخة من بيانات المقال للتعديل
    let processedArticleData = { ...articleData };
    delete processedArticleData.content; // حذف المحتوى مؤقتاً

    // إنشاء معرف فريد للمقال
    const articleRef = doc(collection(db, 'articles'));
    const articleId = articleRef.id;

    // تخزين المحتوى في Storage أولاً
    const contentRef = ref(storage, `articles/${articleId}/content.txt`);
    await uploadString(contentRef, articleData.content);
    const contentUrl = await getDownloadURL(contentRef);

    // إضافة معاينة قصيرة ورابط المحتوى
    processedArticleData = {
      ...processedArticleData,
      id: articleId,
      contentUrl,
      content: articleData.content.substring(0, 500), // معاينة قصيرة جداً
      fullContentInStorage: true,
      createdAt: serverTimestamp()
    };

    // حفظ البيانات في Firestore
    await setDoc(articleRef, processedArticleData);

    return articleId;
  } catch (error) {
    console.error('Error adding article:', error);
    throw error;
  }
};

// تعديل وظيفة قراءة المقال
export const getArticle = async (articleId) => {
  try {
    const articleDoc = await getDoc(doc(db, 'articles', articleId));
    if (!articleDoc.exists()) {
      throw new Error('Article not found');
    }

    const articleData = articleDoc.data();

    // إذا كان المحتوى في Storage، نقوم بجلبه
    if (articleData.contentUrl) {
      const contentRef = ref(storage, `articles/${articleId}/content.txt`);
      const content = await getDownloadURL(contentRef);
      const response = await fetch(content);
      articleData.content = await response.text();
    }

    return articleData;
  } catch (error) {
    console.error('Error getting article:', error);
    throw error;
  }
};

// جلب مقالات مستخدم معين
export const getArticlesByUser = async (userId) => {
  try {
    const articlesRef = collection(db, "articles");
    const q = query(
      articlesRef,
      where("authorId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const snapshot = await getDocs(q);
    const articles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return articles;
  } catch (error) {
    console.error("Error fetching user articles:", error);
    return [];
  }
};
